import { EVENT_FEATURE_MAP, EVENT_FEATURES, METRIC_FEATURES } from "./constants";
import type { CombinedWindow, JsonObject, MetricRecord, SecurityEvent } from "../types";

interface MetricBucket {
  count: number;
  totals: Record<string, number>;
  scenario?: string;
}

interface EventBucket {
  counts: Record<string, number>;
  processes: Set<string>;
  users: Set<string>;
  sources: Set<string>;
}

function epoch(value: unknown): number | null {
  const parsed = Date.parse(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function key(host: string, timestamp: unknown): string | null {
  const value = epoch(timestamp);
  if (value === null) return null;
  return `${host}\u001f${Math.floor(value / 60_000) * 60_000}`;
}

function objectAt(value: unknown, ...path: string[]): unknown {
  let current: unknown = value;
  for (const name of path) {
    if (!current || typeof current !== "object" || Array.isArray(current)) return undefined;
    current = (current as JsonObject)[name];
  }
  return current;
}

export function buildCombinedWindows(
  metrics: MetricRecord[],
  events: SecurityEvent[],
): CombinedWindow[] {
  const metricBuckets = new Map<string, MetricBucket>();
  const eventBuckets = new Map<string, EventBucket>();

  for (const row of metrics) {
    const bucketKey = key(String(row.host || "unknown"), row.timestamp);
    if (!bucketKey) continue;
    const bucket = metricBuckets.get(bucketKey) ?? {
      count: 0,
      totals: Object.fromEntries(METRIC_FEATURES.map((name) => [name, 0])),
    };
    bucket.count += 1;
    for (const name of METRIC_FEATURES) {
      const value = Number(row[name] ?? 0);
      bucket.totals[name] += Number.isFinite(value) ? value : 0;
    }
    if (row.scenario) bucket.scenario = row.scenario;
    metricBuckets.set(bucketKey, bucket);
  }

  for (const event of events) {
    const feature = EVENT_FEATURE_MAP[event.event_type];
    if (!feature) continue;
    const bucketKey = key(String(event.host || "unknown"), event.timestamp);
    if (!bucketKey) continue;
    const bucket = eventBuckets.get(bucketKey) ?? {
      counts: {},
      processes: new Set<string>(),
      users: new Set<string>(),
      sources: new Set<string>(),
    };
    bucket.counts[feature] = (bucket.counts[feature] ?? 0) + 1;
    const process = objectAt(event.data, "process", "name");
    const user = objectAt(event.data, "actor", "user", "name");
    const source =
      objectAt(event.data, "source_endpoint", "ip") ??
      objectAt(event.data, "source_endpoint", "hostname");
    if (process && process !== "unknown") bucket.processes.add(String(process));
    if (user && user !== "unknown") bucket.users.add(String(user));
    if (source && source !== "unknown" && source !== "local") bucket.sources.add(String(source));
    eventBuckets.set(bucketKey, bucket);
  }

  return [...metricBuckets.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([bucketKey, metricBucket]) => {
      const [host, rawEpoch] = bucketKey.split("\u001f");
      const eventBucket = eventBuckets.get(bucketKey);
      const row: CombinedWindow = {
        timestamp: new Date(Number(rawEpoch)).toISOString(),
        host,
        window_seconds: 60,
        metric_sample_count: metricBucket.count,
        event_record_count: 0,
      };
      for (const name of METRIC_FEATURES) {
        row[name] = metricBucket.totals[name] / metricBucket.count;
      }
      for (const name of EVENT_FEATURES) row[name] = 0;
      if (eventBucket) {
        for (const name of Object.values(EVENT_FEATURE_MAP)) {
          row[name] = eventBucket.counts[name] ?? 0;
        }
        row.event_unique_processes = eventBucket.processes.size;
        row.event_unique_users = eventBucket.users.size;
        row.event_unique_remote_sources = eventBucket.sources.size;
      }
      row.event_total_count = Object.values(EVENT_FEATURE_MAP).reduce(
        (total, name) => total + Number(row[name] ?? 0),
        0,
      );
      row.event_record_count = Number(row.event_total_count);
      if (metricBucket.scenario) row.scenario = metricBucket.scenario;
      return row;
    });
}
